<template>
  <div>
    <div class="header profile" />
    <div class="container">
      <div class="box is-large gutter">
        <h1 class="title is-1">
          Checkout
        </h1>

        <div
          v-if="!loaded"
          class="columns"
        >
          <div class="column is-12 has-text-centered">
            <br>
            <p
              v-if="loadError"
              class="help is-danger"
              role="alert"
            >
              {{ loadError }}
            </p>
            <span
              v-else
              class="loader xl"
              style="display: inline-block; width: 3em; height: 3em;"
            />
          </div>
        </div>

        <div v-if="loaded">
          <div
            v-for="(item, i) in order.items"
            :key="`${i}-${item}`"
            class="columns is-mobile is-vcentered track"
          >
            <div
              class="column cover is-narrow is-hidden-mobile"
            >
              <img :src="tracks[item.track].cover_thumbnail">
            </div>
            <div class="column">
              <p class="text label-medium track-title">
                {{ tracks[item.track].display_title }}
              </p>
              <p class="text small track-artist">
                {{ tracks[item.track].artist.name }}
              </p>
              <p
                v-if="item.track.instrumental_version"
                class="text small"
              >
                <span
                  class="icon-static small info"
                  style="margin: -4px 0 -4px 0;"
                />
                Includes instrumental version
              </p>
              <span class="tag is-small is-hidden-tablet">Custom license</span>
            </div>
            <div class="column is-hidden-mobile">
              <h3 class="title is-4">
                Custom license
              </h3>
              <p>{{ item.description }}</p>
            </div>
            <div class="column is-narrow">
              {{ item.unit_amount|formattedDbAmount(currencySymbol) }}
            </div>
          </div>
          <div class="total">
            <h3 class="title is-3 is-pulled-left">
              Amount due
            </h3>
            <span class="is-pulled-right text caption">{{ order.amount_due|formattedDbAmount(currencySymbol) }}</span>
          </div>
        </div>

        <form class="form">
          <div class="columns">
            <div class="column is-half">
              <h4 class="title is-4">
                Billing details
              </h4>

              <div class="field">
                <div class="control">
                  <input
                    v-model="billing_details.name"
                    class="input"
                    type="text"
                    required
                    placeholder="Cardholder name"
                  >
                </div>
              </div>

              <div class="field">
                <div :id="cardElId" />

                <p
                  v-if="cardError"
                  id="card-errors"
                  class="help is-danger"
                  role="alert"
                >
                  {{ cardError }}
                </p>
              </div>
              <figure>
                <img
                  src="@/assets/card-brands.png"
                  alt="Accepted card brands: Visa, Mastercard, American Express, Maestro."
                >
              </figure>
            </div>

            <div class="column is-half">
              <h3 class="title is-3">
                Invoice details <small class="dim">
                  [optional]
                </small>
              </h3>

              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">Company</label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <div class="control">
                      <input
                        id="id_billing_company_name"
                        v-model="teamData.billing_company_name"
                        type="text"
                        name="billing_company_name"
                        class="input"
                      >
                    </div>
                  </div>
                </div>
              </div>
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">Contact</label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <div class="control">
                      <input
                        id="id_billing_contact"
                        v-model="teamData.billing_contact"
                        type="text"
                        name="billing_contact"
                        class="input"
                      >
                    </div>
                  </div>
                </div>
              </div>
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">Address 1</label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <div class="control">
                      <input
                        id="id_address_1"
                        v-model="teamData.address_1"
                        type="text"
                        name="address_1"
                        class="input"
                      >
                    </div>
                  </div>
                </div>
              </div>
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">Address 2</label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <div class="control">
                      <input
                        id="id_address_2"
                        v-model="teamData.address_2"
                        type="text"
                        name="address_2"
                        class="input"
                      >
                    </div>
                  </div>
                </div>
              </div>
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">City</label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <div class="control">
                      <input
                        id="id_city"
                        v-model="teamData.city"
                        type="text"
                        name="city"
                        class="input"
                      >
                    </div>
                  </div>
                </div>
              </div>
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">County/area</label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <div class="control">
                      <input
                        id="id_county"
                        v-model="teamData.county"
                        type="text"
                        name="county"
                        class="input"
                      >
                    </div>
                  </div>
                </div>
              </div>
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">Postal code</label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <div class="control">
                      <input
                        id="id_postal_code"
                        v-model="teamData.postal_code"
                        type="text"
                        name="postal_code"
                        class="input"
                      >
                    </div>
                  </div>
                </div>
              </div>
              <div class="field is-horizontal">
                <div class="field-label is-normal">
                  <label class="label">Country</label>
                </div>
                <div class="field-body">
                  <div class="field">
                    <div class="control">
                      <div class="select">
                        <select
                          v-model="teamData.country"
                        >
                          <option :value="''">
                            --------
                          </option>
                          <option
                            v-for="option in countryOptions"
                            :key="'checkout-country-' + option.value"
                            :value="option.value"
                          >
                            {{ option.display_name }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="footer">
            <button
              class="button is-primary is-large is-pulled-right"
              :class="{ 'is-loading': busy }"
              :disabled="!canSubmit || busy"
              @click.prevent="handlePayClick"
            >
              Confirm &amp; Pay
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import orderClient from '@/api/orders.js'
import teamClient from '@/api/teams.js'
import trackClient from '@/api/tracks.js'
import { formatComponentName } from '@/utils/common.js'

export default {
  name: 'CustomOrderCartCheckout',

  props: {
    orderId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      billing_details: {
        name: ''
      },
      teamData: {
        name: '', // Don't change the name in this component. Needed for API though.
        billing_company_name: '',
        billing_contact: '',
        address_1: '',
        address_2: '',
        city: '',
        county: '',
        postal_code: '',
        country: ''
      },
      countryOptions: [],
      clientSecret: null,
      cardElId: 'card-element',
      cardElSelector: '#card-element',
      cardComplete: false,
      cardError: null,
      stripe: null,
      stripeElements: null,
      stripeCard: null,
      order: null,
      tracks: {},
      busy: false,
      loaded: false,
      loadError: null,
      paymentTaken: false
    }
  },

  computed: {
    canSubmit: function () {
      return this.cardComplete && this.billing_details.name && !this.paymentTaken
    },

    componentName: function () {
      return formatComponentName(this)
    },

    currencySymbol: function () {
      if (this.order !== null) {
        return this.order.currency.symbol
      }
      return ''
    }
  },

  mounted () {
    this.stripe = window.Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY)
    this.stripeElements = this.stripe.elements()

    // Custom styling can be passed to options when creating an Element.
    // (Note that this demo uses a wider set of styles than the guide below.)
    const style = {
      base: {
        color: '#32325d',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': {
          color: '#aab7c4'
        }
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a'
      }
    }

    // Create an instance of the card Element.
    this.stripeCard = this.stripeElements.create('card', { style })
    this.stripeCard.on('change', (event) => {
      this.cardComplete = event.complete
    })
    this.stripeCard.mount(this.cardElSelector)

    // No way to guarantee we have the team data without loading it first.
    this.$store.dispatch('user/setMe').then(() => {
      teamClient.options()
        .then((response) => {
          this.countryOptions = response.data.actions.POST.country.choices
          this.loadTeamData()
        })
    })
  },

  created () {
    const vm = this

    vm.loaded = false

    orderClient.retrieve(vm.orderId)
      .then((response) => {
        if (response.data.status === 'open') {
          vm.order = response.data

          const promises = []

          if (response.data.payment_intent_client_secret !== null) {
            vm.clientSecret = response.data.payment_intent_client_secret
          } else {
            // updateOrder sets clientSecret for us.
            promises.push(vm.updateOrder())
          }

          vm.order.items.forEach(function (item) {
            promises.push(
              trackClient.retrieve(item.track).then(response => {
                vm.$set(vm.tracks, item.track, response.data)
              })
            )
          })
          return Promise.all(promises).then(() => {
            vm.loaded = true
            vm.busy = false
          })
        } else {
          this.$router.push({ name: 'not-found' })
        }
      })
      .catch(error => {
        if (error.response && error.response.status === 404) {
          this.$router.push({ name: 'not-found' })
          return
        }
        vm.loadError = 'We couldn\'t load this order. Please refresh the page to try again.'
        return Promise.reject(error)
      })
  },

  methods: {
    loadTeamData: function () {
      // Load default invoice data from team profile.
      const team = Object.assign({}, this.$store.getters['user/currentTeam'])

      // Copy logged in team data to local `teamData`.
      const teamKeys = Object.keys(this.teamData)
      for (const [key, value] of Object.entries(team)) {
        if (teamKeys.includes(key)) {
          this.teamData[key] = value
        }
      }
    },

    updateOrder: function () {
      /* We need to push an empty partial update to the API to get a
       * PaymentIntent created for this order.
       */
      const vm = this

      const orderData = {}
      return orderClient.partialUpdate(vm.orderId, orderData)
        .then((response) => {
          vm.order = response.data
          vm.clientSecret = response.data.payment_intent_client_secret
        })
    },

    handlePayClick: function () {
      const vm = this
      const user = this.$store.getters['user/currentUser']
      // What to tell the user if the current step fails.
      let errorMessage = 'There was a problem saving your invoice details. Please check them and try again.'
      let isCardError = false

      vm.cardError = null
      vm.busy = true

      teamClient
        .partialUpdate({ team: this.teamData, user })
        .then(() => {
          errorMessage = 'Something went wrong taking payment. Please try again.'
          return vm.stripe.confirmCardPayment(
            vm.clientSecret,
            {
              payment_method: {
                card: vm.stripeCard,
                billing_details: vm.billing_details
              }
            }
          )
        })
        .then((response) => {
          // Stripe resolves with either an error or a paymentIntent.
          if (response.error) {
            errorMessage = response.error.message
            isCardError = true
            throw response.error
          }

          // The card has been charged. The payment_intent.succeeded webhook completes the
          // order even if finalizing fails here, so from now on don't invite another payment.
          vm.paymentTaken = true
          errorMessage = 'Your payment was successful but we couldn\'t load your licenses yet. They will appear on your licenses page shortly, so please don\'t pay again.'

          // Activate/finalize the order so we get the licenses right away.
          return orderClient.finalize(vm.order.pk, {
            payment_intent: response.paymentIntent.id
          })
        })
        .then(() => {
          vm.busy = false
          vm.$router.push({
            name: 'license-list',
            params: {
              showCheckoutSuccess: true,
              tracksPurchased: vm.order.items.length
            }
          })
        })
        .catch((error) => {
          vm.cardError = errorMessage
          vm.busy = false
          // Declined cards etc. are expected; anything else should reach Sentry.
          if (!isCardError) {
            return Promise.reject(error)
          }
        })
    }
  },

  metaInfo: {
    title: 'Checkout'
  }
}
</script>

<style scoped lang="scss">
  .total {
    clear: both;
    overflow: hidden;
  }

  .StripeElement {
    box-sizing: border-box;

    height: 40px;

    padding: 10px 12px;

    border: 1px solid transparent;
    border-radius: 4px;
    background-color: white;

    box-shadow: 0 1px 3px 0 #e6ebf1;
    -webkit-transition: box-shadow 150ms ease;
    transition: box-shadow 150ms ease;
  }

  .StripeElement--focus {
    box-shadow: 0 1px 3px 0 #cfd7df;
  }

  .StripeElement--invalid {
    border-color: #fa755a;
  }

  .StripeElement--webkit-autofill {
    background-color: #fefde5 !important;
  }
</style>
